/**
 * The match step: one pure function from the previous state and this tick's inputs to the next
 * state, plus the static-context builder and a thin runner for the client and the server.
 *
 * Tick order (mech 0 before mech 1 everywhere):
 *   clone → sync barriers into the world → inputs (bots decide from the previous state) →
 *   modifiers → effect actions → abilities → modifiers again → movement + separation + dash
 *   impacts → heat, melee, weapons → projectiles → deployables → shields, ultimate trickle,
 *   kill volume → flow → effect expiry → remember inputs.
 */
import { featuresOf } from '../arena/features.js';
import { moduleCapacityScales, moduleMods } from '../data/index.js';
import { clamp, wrapAngle } from '../sim/math.js';
import { capsuleOf, stepMech } from '../sim/step-mech.js';
import { stepAbilities } from './abilities.js';
import { carryOnPlatforms, stepArena, stepPlatformContact } from './arena.js';
import { catalogOf, magazineSizes } from './catalog.js';
import { applyDamage, areaPacket } from './damage.js';
import { blockersOf, stepDeployables } from './deployables.js';
import { knockbackFrom } from './effects.js';
import { consumeImpulses, expireEffects, tickEffectActions } from './effects-tick.js';
import { applyKillVolume, stepFlow } from './flow.js';
import { computeModifiers, moveModifiersOf } from './modifiers.js';
import { stepProjectiles } from './projectiles.js';
import { stepHeat, stepShields, stepUltTrickle } from './resources.js';
import { separateCapsules } from './separation.js';
import { cloneMatchState, combatantCapsule, createBotState, createMatchState, releasedInput, secondsToTicks, } from './state.js';
import { emit } from './tick.js';
import { otherSlot, } from './types.js';
import { stepMelee, stepWeapons } from './weapons.js';
/** A bot that stands still and keeps its aim (tests, and the `idle` difficulty). */
export const idleBot = (prev, slot) => {
    const c = prev.mechs[slot];
    const bot = c.bot ?? createBotState('idle', c.body.position, c.body.yaw);
    return { input: releasedInput(c.lastInput), bot: { ...bot } };
};
/** Cover points beside tall arena boxes (pillars, walls, towers, platforms). */
export function coverPointsOf(arena) {
    const points = [];
    const { min, max } = arena.bounds;
    for (const box of arena.colliders) {
        const [hx, hy, hz] = box.halfExtents;
        if (hy < 2.4 || (hx > 25 && hz > 25) || box.rotation?.[0] || box.rotation?.[2])
            continue;
        const [cx, cy, cz] = box.center;
        const y = cy - hy;
        const gap = 2.4;
        const candidates = [
            { x: cx + hx + gap, y, z: cz },
            { x: cx - hx - gap, y, z: cz },
            { x: cx, y, z: cz + hz + gap },
            { x: cx, y, z: cz - hz - gap },
        ];
        for (const p of candidates) {
            if (p.x < min[0] + 2 || p.x > max[0] - 2 || p.z < min[2] + 2 || p.z > max[2] - 2)
                continue;
            points.push(p);
        }
    }
    return points;
}
export function arenaInfoOf(arena) {
    if (!arena) {
        return {
            bounds: { min: { x: -200, y: -50, z: -200 }, max: { x: 200, y: 100, z: 200 } },
            cover: [],
            features: featuresOf(null),
        };
    }
    return {
        bounds: {
            min: { x: arena.bounds.min[0], y: arena.bounds.min[1], z: arena.bounds.min[2] },
            max: { x: arena.bounds.max[0], y: arena.bounds.max[1], z: arena.bounds.max[2] },
        },
        cover: coverPointsOf(arena),
        features: featuresOf(arena),
    };
}
export function buildMatchStatic(setup, arena, catalog = catalogOf(setup)) {
    const mechOf = (id) => {
        const def = catalog.mechs[id];
        if (!def)
            throw new Error(`unknown mech "${id}"`);
        return def;
    };
    return {
        defs: [
            seatDefinition(mechOf(setup.mechs[0]), setup.loadouts?.[0] ?? null, catalog),
            seatDefinition(mechOf(setup.mechs[1]), setup.loadouts?.[1] ?? null, catalog),
        ],
        balance: catalog.balance,
        weapons: catalog.weapons,
        arena: arenaInfoOf(arena),
        magazines: magazineSizes(catalog.weapons),
        // Module sets are folded once here and applied before every tick's status effects.
        baseMods: [
            moduleMods(setup.loadouts?.[0]?.modules ?? []),
            moduleMods(setup.loadouts?.[1]?.modules ?? []),
        ],
    };
}
/**
 * A seat's definition (Phase 8): the mech with its capacity modules applied (hull and shield
 * multiply once here, so every `def.hp` read stays right) and its primary swapped for a listed
 * alternate. The stock definition is never mutated; unknown or unlisted choices fall back.
 */
export function seatDefinition(def, loadout, catalog) {
    if (!loadout)
        return def;
    const scales = moduleCapacityScales(loadout.modules);
    const alt = loadout.weapons.primary;
    const swap = alt !== null && def.alternates.primary.includes(alt) && catalog.weapons[alt];
    const weapons = swap
        ? def.weapons.map((w) => (w.slot === 'primary' ? { ...w, id: alt } : w))
        : def.weapons;
    // A melee alternate also becomes the melee weapon, so the runtime's melee spec follows it.
    const melee = swap && catalog.weapons[alt]?.kind === 'melee' ? alt : def.melee;
    if (scales.hp === 1 && scales.shield === 1 && !swap)
        return def;
    return {
        ...def,
        hp: def.hp * scales.hp,
        shield: def.shield * scales.shield,
        weapons,
        ...(melee !== undefined ? { melee } : {}),
    };
}
export function createMatch(setup, ctx) {
    return createMatchState(setup, ctx, ctx.magazines);
}
/**
 * Clamps an input to what the rules accept: axes in [-1, 1], a finite yaw wrapped to (-pi, pi],
 * pitch within the vertical range. The server validates packets with the Zod schema first; this
 * is the last line for both sides so the same input means the same thing everywhere.
 */
export function sanitizeInput(input) {
    return {
        ...input,
        moveX: clamp(Number.isFinite(input.moveX) ? input.moveX : 0, -1, 1),
        moveY: clamp(Number.isFinite(input.moveY) ? input.moveY : 0, -1, 1),
        aimYaw: Number.isFinite(input.aimYaw) ? wrapAngle(input.aimYaw) : 0,
        aimPitch: clamp(Number.isFinite(input.aimPitch) ? input.aimPitch : 0, -Math.PI / 2, Math.PI / 2),
    };
}
/** Inputs for a phase that allows no actions: aim only. */
function aimOnly(input) {
    return { ...releasedInput(input), seq: input.seq };
}
const scratchMove = {
    x: 0,
    y: 0,
    z: 0,
    grounded: false,
    hitWall: false,
    hitCeiling: false,
};
function separateMechs(t) {
    const a = t.s.mechs[0];
    const b = t.s.mechs[1];
    if (!a.alive || !b.alive)
        return;
    const sep = separateCapsules({ capsule: combatantCapsule(a, t.ctx.defs[0]), weight: t.ctx.defs[0].weightTons }, { capsule: combatantCapsule(b, t.ctx.defs[1]), weight: t.ctx.defs[1].weightTons });
    if (!sep)
        return;
    for (const slot of [0, 1]) {
        const c = t.s.mechs[slot];
        const push = slot === 0 ? sep.a : sep.b;
        t.world.moveCapsule(c.body.position, push, capsuleOf(t.ctx.defs[slot]), scratchMove);
        c.body.position = { x: scratchMove.x, y: scratchMove.y, z: scratchMove.z };
    }
}
/** A running dash that carries damage hits the enemy once when the capsules meet. */
function dashImpacts(t, slot) {
    const dash = t.mods[slot].dash;
    if (!dash || dash.hit || dash.damage <= 0)
        return;
    const enemy = otherSlot(slot);
    const me = t.s.mechs[slot];
    const other = t.s.mechs[enemy];
    if (!me.alive || !other.alive)
        return;
    const ra = t.ctx.defs[slot].capsule.radius;
    const rb = t.ctx.defs[enemy].capsule.radius;
    const dx = other.body.position.x - me.body.position.x;
    const dz = other.body.position.z - me.body.position.z;
    const dy = Math.abs(other.body.position.y - me.body.position.y);
    if (Math.hypot(dx, dz) > ra + rb + 0.6 || dy > 3)
        return;
    dash.hit = true;
    applyDamage(t, areaPacket(t, enemy, slot, dash.damage, 'ability', me.body.position));
    if (dash.knockback > 0)
        knockbackFrom(t, enemy, slot, me.body.position, dash.knockback, 1.5);
    if (dash.stopOnHit) {
        for (const e of me.effects)
            if (e.params.dash === dash)
                e.ticksLeft = 1;
    }
}
/**
 * Advances the match by one tick. `inputs` holds the human inputs by slot (null for a bot slot
 * or a missing packet, which repeats the last aim with every button released). Never mutates
 * `prev`. The context may carry the Phase 4 hooks: rewound enemy poses for lag compensation
 * and the prediction flag (damage is skipped; the server decides it).
 */
export function stepMatch(prev, inputs, context) {
    const s = cloneMatchState(prev);
    s.tick++;
    const { world } = context;
    const ctx = context.static;
    world.syncBlockers(blockersOf(s, ctx.arena.features));
    const t = {
        s,
        ctx,
        world,
        mods: [
            computeModifiers(s.mechs[0], ctx.defs[0], ctx.balance, ctx.baseMods[0]),
            computeModifiers(s.mechs[1], ctx.defs[1], ctx.balance, ctx.baseMods[1]),
        ],
        ...(context.rewound ? { rewound: context.rewound } : {}),
        ...(context.prediction !== undefined && context.prediction !== null
            ? { prediction: context.prediction }
            : {}),
    };
    const magazines = ctx.magazines;
    // --- inputs ---
    const resolved = [prev.mechs[0].lastInput, prev.mechs[1].lastInput];
    for (const slot of [0, 1]) {
        const c = s.mechs[slot];
        let input;
        if (c.bot && c.bot.level !== 'idle') {
            const decision = context.bot(prev, slot, ctx, world, s.rng);
            c.bot = decision.bot;
            input = decision.input;
        }
        else if (c.bot) {
            const decision = idleBot(prev, slot, ctx, world, s.rng);
            c.bot = decision.bot;
            input = decision.input;
        }
        else {
            input = inputs[slot] ?? releasedInput(c.lastInput);
        }
        input = sanitizeInput(input);
        const phase = s.flow.phase;
        if (phase === 'countdown' || phase === 'roundEnd' || phase === 'matchEnd' || !c.alive) {
            input = aimOnly(input);
        }
        resolved[slot] = input;
    }
    // --- effects and abilities ---
    for (const slot of [0, 1])
        tickEffectActions(t, slot);
    for (const slot of [0, 1])
        stepAbilities(t, slot, resolved[slot]);
    t.mods = [
        computeModifiers(s.mechs[0], ctx.defs[0], ctx.balance, ctx.baseMods[0]),
        computeModifiers(s.mechs[1], ctx.defs[1], ctx.balance, ctx.baseMods[1]),
    ];
    // --- movement ---
    carryOnPlatforms(t);
    for (const slot of [0, 1]) {
        const c = s.mechs[slot];
        if (!c.alive)
            continue;
        c.body = stepMech(c.body, resolved[slot], world, ctx.defs[slot], undefined, moveModifiersOf(t.mods[slot]));
        consumeImpulses(t, slot);
    }
    separateMechs(t);
    for (const slot of [0, 1])
        dashImpacts(t, slot);
    stepPlatformContact(t);
    stepArena(t);
    // --- combat ---
    for (const slot of [0, 1]) {
        stepHeat(t, slot, resolved[slot]);
        stepMelee(t, slot, resolved[slot]);
        stepWeapons(t, slot, resolved[slot]);
    }
    stepProjectiles(t);
    stepDeployables(t);
    for (const slot of [0, 1]) {
        stepShields(t, slot);
        stepUltTrickle(t, slot);
    }
    applyKillVolume(t);
    // --- flow and bookkeeping ---
    stepFlow(t, prev, magazines);
    for (const slot of [0, 1])
        expireEffects(t, slot);
    for (const slot of [0, 1])
        s.mechs[slot].lastInput = resolved[slot];
    return s;
}
/** Wraps the pure step for the client (practice and prediction) and the Colyseus room. */
export class MatchRunner {
    state;
    static;
    world;
    bot;
    constructor(setup, world, arena, bot, catalog) {
        this.static = buildMatchStatic(setup, arena, catalog);
        this.world = world;
        this.bot = bot;
        this.state = createMatch(setup, this.static);
        this.world.syncBlockers([]);
    }
    /**
     * Advances one tick with the given human inputs and returns the events it produced. The
     * options carry the Phase 4 hooks (rewound enemy poses, the prediction flag).
     */
    step(inputs, options = {}) {
        this.state = stepMatch(this.state, inputs, {
            static: this.static,
            world: this.world,
            bot: this.bot,
            ...(options.rewound ? { rewound: options.rewound } : {}),
            ...(options.prediction !== undefined ? { prediction: options.prediction } : {}),
        });
        return this.state.events;
    }
    get finished() {
        return this.state.flow.phase === 'matchEnd';
    }
    /** Replaces the state (resume from a snapshot); the world resyncs on the next step. */
    restore(state) {
        this.state = cloneMatchState(state);
    }
    // --- practice-only hooks (no server path ever exposes these) ---
    assertPractice(what) {
        if (this.state.config.mode !== 'practice')
            throw new Error(`${what} is only available in practice`);
    }
    setBotLevel(slot, level) {
        this.assertPractice('setBotLevel');
        const s = cloneMatchState(this.state);
        s.config.bots[slot] = level;
        const c = s.mechs[slot];
        c.bot = level
            ? { ...createBotState(level, c.body.position, c.body.yaw), ...(c.bot ?? {}), level }
            : null;
        this.state = s;
    }
    skipCountdown() {
        this.assertPractice('skipCountdown');
        if (this.state.flow.phase !== 'countdown')
            return;
        const s = cloneMatchState(this.state);
        s.flow.ticksLeft = 1;
        this.state = s;
    }
    setRoundSeconds(seconds) {
        this.assertPractice('setRoundSeconds');
        const s = cloneMatchState(this.state);
        s.config.roundSeconds = seconds;
        if (s.flow.phase === 'live')
            s.flow.ticksLeft = Math.min(s.flow.ticksLeft, secondsToTicks(seconds));
        this.state = s;
    }
    ultimateReady(slot) {
        this.assertPractice('ultimateReady');
        const s = cloneMatchState(this.state);
        s.mechs[slot].ultCharge = 100;
        this.state = s;
    }
    /** Puts a mech's feet at a point with no momentum (tests reach pads, pickups and platforms). */
    teleport(slot, position) {
        this.assertPractice('teleport');
        const s = cloneMatchState(this.state);
        const c = s.mechs[slot];
        c.body.position = { x: position.x, y: position.y, z: position.z };
        c.body.velocity = { x: 0, y: 0, z: 0 };
        c.body.grounded = false;
        c.platform = null;
        this.state = s;
    }
}
/** Convenience for tests: emits an event into a state (never used by the rules). */
export function pushEvent(t, event) {
    emit(t, event);
}
//# sourceMappingURL=match.js.map